import React, { useMemo } from 'react';
import { User } from '../../types/user';
import { useOrganizationContext } from '../../context/OrganizationContext';
import { getHashMap } from '../../utils/objectTransformers';
import { ValidationErrors } from '../../hooks/useForm';
import { UserDisplay } from '../UserDisplay/UserDisplay';
import { AdaptiveSelect } from '../AdaptiveSelect/AdaptiveSelect';
import useSWR from 'swr';
import { useClient } from '../../hooks/useClient';
import { classNames } from 'primereact/utils';
import { trim } from '../../utils/textUtils';

interface UserOption extends User {
  label: string;
  value: string;
}

// Every caller picks one employee (reservation, client panel, client edit dialog), so the select is single only.
interface Props {
  onChange: (user: User | undefined) => void;
  value?: string;
  id?: string;
  error?: string | (ValidationErrors<User> | undefined)[];
  className?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  feature?: string;
  filterByBranchIds?: string[];
  disabled?: boolean;
}

const getUsersUrl = (organizationId: string, params: { feature?: string; branches?: string }) => {
  const queryParams = Object.entries(params)
    .filter(([_, value]) => value)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');

  return `/organizations/${organizationId}/users${queryParams ? `?${queryParams}` : ''}`;
};

export const UsersSelect = ({
  onChange,
  id = 'users-dropdown',
  value,
  error,
  className,
  placeholder = 'Elige empleados',
  searchPlaceholder = 'Buscar empleados',
  feature,
  filterByBranchIds,
  disabled = false,
}: Props) => {
  const { get } = useClient();
  const { organization } = useOrganizationContext();

  const { data: users, isLoading } = useSWR(
    organization ? getUsersUrl(organization.id, { feature, branches: filterByBranchIds?.join(',') }) : null,
    get<User[]>,
  );

  const options = useMemo(() => {
    if (!users) return [];
    return users.map((user: User) => ({
      ...user,
      label: `${trim(user.name)} ${trim(user.surnames)}`,
      value: user.id,
    }));
  }, [users]);

  const usersHashMap = useMemo(() => getHashMap(users ?? [], 'id'), [users]);

  // An empty value has no entry in the map, so it also reports undefined.
  const handleChange = (selectedValue: string) => {
    onChange(usersHashMap[selectedValue]);
  };

  // Only called with an option of the list (AdaptiveSelect shows the placeholder itself when nothing is chosen).
  const userTemplate = (option: UserOption) => <UserDisplay fullName={option.label} avatarUrl={option.avatarUrl} />;

  return (
    <AdaptiveSelect
      id={id}
      placeholder={placeholder}
      value={value}
      className={classNames(className, { 'p-invalid': error })}
      invalid={!!error}
      options={options}
      itemTemplate={userTemplate}
      onChange={handleChange}
      modalTitle='Seleccionar empleado'
      searchPlaceholder={searchPlaceholder}
      loading={isLoading}
      disabled={disabled}
      emptyMessage='No hay empleados'
      emptyFilterMessage='No se han encontrado empleados'
    />
  );
};
